/* ==========================================================================
   APPLION × ESSENTIAL CART DRAWER
   FINAL CART SYNC BRIDGE
   --------------------------------------------------------------------------
   - Essential Cart Drawer owns cart UI
   - Applion does NOT create a second cart drawer
   - Header count syncs from real Shopify cart
   - Handles add / remove / quantity changes
   - Header cart icon opens Essential drawer
   ========================================================================== */

(() => {
  'use strict';

  if (window.__applionEssentialCartSyncInitialized) return;

  window.__applionEssentialCartSyncInitialized = true;


  /* ==========================================================================
     CONFIG
     ========================================================================== */

  const SELECTORS = {
    cartIcon: '#cart-icon-bubble',

    countBubble: '.cart-count-bubble',

    addButton: '[data-applion-add-button]'
  };


  let syncTimer = null;


  /* ==========================================================================
     SHOPIFY ROUTES
     ========================================================================== */

  const getRootUrl = () => {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  };


  const getCartJsonUrl = () => {
    return `${getRootUrl()}cart.js`;
  };


  /* ==========================================================================
     CART ENDPOINT DETECTION
     ========================================================================== */

  const isCartMutation = (url, method) => {
    if (!url) return false;

    const requestMethod =
      String(method || 'GET').toUpperCase();

    if (
      requestMethod !== 'POST' &&
      requestMethod !== 'PUT' &&
      requestMethod !== 'PATCH' &&
      requestMethod !== 'DELETE'
    ) {
      return false;
    }


    const value =
      String(url);


    return (
      value.includes('/cart/add') ||
      value.includes('/cart/change') ||
      value.includes('/cart/update') ||
      value.includes('/cart/clear')
    );
  };


  /* ==========================================================================
     GET REAL SHOPIFY CART
     ========================================================================== */

  const getShopifyCart = async () => {
    const response =
      await fetch(
        getCartJsonUrl(),
        {
          method: 'GET',

          headers: {
            Accept: 'application/json'
          },

          credentials: 'same-origin',

          cache: 'no-store'
        }
      );


    if (!response.ok) {
      throw new Error(
        'Unable to read Shopify cart.'
      );
    }


    return response.json();
  };


  /* ==========================================================================
     HEADER COUNT UPDATE
     ========================================================================== */

  const updateHeaderCount = (count) => {
    const cartIcon =
      document.querySelector(
        SELECTORS.cartIcon
      );


    if (!cartIcon) return;


    const itemCount =
      Math.max(
        0,
        Number(count || 0)
      );


    let bubble =
      cartIcon.querySelector(
        SELECTORS.countBubble
      );


    /* ----------------------------------------------------------------------
       CREATE BUBBLE IF MISSING

       Your header visibly uses 0, so we keep the bubble even for empty cart.
       ---------------------------------------------------------------------- */

    if (!bubble) {

      bubble =
        document.createElement('div');


      bubble.className =
        'cart-count-bubble';


      const visibleCount =
        document.createElement('span');


      visibleCount.setAttribute(
        'aria-hidden',
        'true'
      );


      const accessibleCount =
        document.createElement('span');


      accessibleCount.className =
        'visually-hidden';


      bubble.appendChild(
        visibleCount
      );


      bubble.appendChild(
        accessibleCount
      );


      cartIcon.appendChild(
        bubble
      );

    }


    /* ----------------------------------------------------------------------
       STANDARD DAWN NUMBER
       ---------------------------------------------------------------------- */

    let visibleCount =
      bubble.querySelector(
        'span[aria-hidden="true"]'
      );


    if (!visibleCount) {

      visibleCount =
        document.createElement('span');


      visibleCount.setAttribute(
        'aria-hidden',
        'true'
      );


      bubble.prepend(
        visibleCount
      );

    }


    visibleCount.textContent =
      String(itemCount);


    /* ----------------------------------------------------------------------
       ACCESSIBILITY COUNT
       ---------------------------------------------------------------------- */

    let accessibleCount =
      bubble.querySelector(
        '.visually-hidden'
      );


    if (!accessibleCount) {

      accessibleCount =
        document.createElement('span');


      accessibleCount.className =
        'visually-hidden';


      bubble.appendChild(
        accessibleCount
      );

    }


    accessibleCount.textContent =
      itemCount === 1
        ? '1 item in cart'
        : `${itemCount} items in cart`;


    /* ----------------------------------------------------------------------
       COMMON CUSTOM HEADER COUNT SELECTORS

       Additional safety for modified Dawn headers.
       ---------------------------------------------------------------------- */

    const additionalCounters =
      document.querySelectorAll(
        [
          '[data-cart-count]',
          '.header__cart-count',
          '.cart-count',
          '.cart-item-count',
          '[data-cart-item-count]'
        ].join(',')
      );


    additionalCounters.forEach(
      (element) => {

        if (
          bubble.contains(element)
        ) {
          return;
        }


        element.textContent =
          String(itemCount);


        if (
          element.hasAttribute(
            'data-cart-count'
          )
        ) {
          element.setAttribute(
            'data-cart-count',
            String(itemCount)
          );
        }

      }
    );
  };


  /* ==========================================================================
     SYNC CART COUNT
     ========================================================================== */

  const syncCartCount = async () => {
    try {

      const cart =
        await getShopifyCart();


      updateHeaderCount(
        cart.item_count
      );


      document.dispatchEvent(
        new CustomEvent(
          'applion:cart-count-updated',
          {
            detail: {
              itemCount:
                Number(
                  cart.item_count || 0
                ),

              cart
            }
          }
        )
      );


    } catch (error) {

      console.warn(
        '[Applion] Cart count sync failed.',
        error
      );

    }
  };


  /* ==========================================================================
     SCHEDULE SYNC
     ========================================================================== */

  const scheduleSync = (
    delay = 80
  ) => {

    if (syncTimer) {

      window.clearTimeout(
        syncTimer
      );

    }


    syncTimer =
      window.setTimeout(
        () => {

          syncTimer = null;


          syncCartCount();

        },
        delay
      );

  };


  /* ==========================================================================
     MULTIPLE SAFETY SYNC ATTEMPTS
     ========================================================================== */

  const queueSync = () => {

    scheduleSync(60);


    window.setTimeout(
      syncCartCount,
      300
    );


    window.setTimeout(
      syncCartCount,
      750
    );

  };


  /* ==========================================================================
     OPEN ESSENTIAL CART DRAWER
     ========================================================================== */

  const openEssentialCart = () => {

    if (
      window.essentialCartApi &&
      typeof window.essentialCartApi
        .openCart === 'function'
    ) {

      try {

        window.essentialCartApi
          .openCart();


        return true;

      } catch (error) {

        console.warn(
          '[Applion] Essential openCart failed.',
          error
        );

      }

    }


    return false;
  };


  /* ==========================================================================
     HEADER CART ICON
     ========================================================================== */

  document.addEventListener(
    'click',
    (event) => {

      const target =
        event.target;


      if (
        !(target instanceof Element)
      ) {
        return;
      }


      const cartIcon =
        target.closest(
          SELECTORS.cartIcon
        );


      if (!cartIcon) return;


      if (openEssentialCart()) {

        event.preventDefault();

        queueSync();

      }

    }
  );


  /* ==========================================================================
     APPLION ADD BUTTON

     Essential Cart Drawer handles the actual add because you configured:
     [data-applion-add-button]

     We only sync the header afterwards.
     ========================================================================== */

  document.addEventListener(
    'click',
    (event) => {

      const target =
        event.target;


      if (
        !(target instanceof Element)
      ) {
        return;
      }


      const addButton =
        target.closest(
          SELECTORS.addButton
        );


      if (!addButton) return;


      /*
        IMPORTANT:
        Do not preventDefault().
        Do not run another cart/add request.
        Essential owns the add-to-cart lifecycle.
      */


      window.setTimeout(
        syncCartCount,
        250
      );


      window.setTimeout(
        syncCartCount,
        600
      );


      window.setTimeout(
        syncCartCount,
        1200
      );

    }
  );


  /* ==========================================================================
     FETCH OBSERVER

     Watches Essential's Shopify cart requests.
     ========================================================================== */

  const installFetchObserver = () => {

    if (
      typeof window.fetch !== 'function'
    ) {
      return;
    }


    if (
      window.fetch
        .__applionCartObserved
    ) {
      return;
    }


    const originalFetch =
      window.fetch.bind(window);


    const wrappedFetch =
      async (...args) => {

        const input =
          args[0];


        const options =
          args[1] || {};


        let url = '';

        let method =
          options.method || 'GET';


        if (
          typeof input === 'string'
        ) {

          url = input;

        } else if (input) {

          url =
            input.url || '';


          if (
            !options.method &&
            input.method
          ) {

            method =
              input.method;

          }

        }


        const cartMutation =
          isCartMutation(
            url,
            method
          );


        const response =
          await originalFetch(
            ...args
          );


        if (
          cartMutation &&
          response.ok
        ) {

          /*
            Shopify cart has already changed
            at this point.
          */

          queueSync();

        }


        return response;

      };


    wrappedFetch
      .__applionCartObserved =
      true;


    window.fetch =
      wrappedFetch;

  };


  /* ==========================================================================
     XHR OBSERVER

     Essential or another cart app may use XMLHttpRequest instead of fetch.
     ========================================================================== */

  const installXhrObserver = () => {

    if (
      XMLHttpRequest.prototype
        .__applionCartObserved
    ) {
      return;
    }


    XMLHttpRequest.prototype
      .__applionCartObserved =
      true;


    const originalOpen =
      XMLHttpRequest.prototype.open;


    const originalSend =
      XMLHttpRequest.prototype.send;


    XMLHttpRequest.prototype.open =
      function (
        method,
        url,
        ...rest
      ) {

        this.__applionCartMutation =
          isCartMutation(
            url,
            method
          );


        return originalOpen.call(
          this,
          method,
          url,
          ...rest
        );

      };


    XMLHttpRequest.prototype.send =
      function (...args) {

        if (
          this.__applionCartMutation
        ) {

          this.addEventListener(
            'loadend',
            () => {

              if (
                this.status >= 200 &&
                this.status < 400
              ) {

                queueSync();

              }

            },
            {
              once: true
            }
          );

        }


        return originalSend.apply(
          this,
          args
        );

      };

  };


  /* ==========================================================================
     SHOPIFY STANDARD CART EVENT
     ========================================================================== */

  document.addEventListener(
    'shopify:cart:lines-update',
    queueSync
  );


  /* ==========================================================================
     COMMON CART EVENTS
     ========================================================================== */

  [
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'cart:change'
  ].forEach(
    (eventName) => {

      document.addEventListener(
        eventName,
        queueSync
      );

    }
  );


  /* ==========================================================================
     ESSENTIAL DRAWER CLICK FALLBACK

     Even if the app has cached its own network function before our observer,
     clicking quantity/remove controls will trigger count verification.

     No dependency on Essential's internal class names.
     ========================================================================== */

  document.addEventListener(
    'click',
    (event) => {

      const target =
        event.target;


      if (
        !(target instanceof Element)
      ) {
        return;
      }


      /*
        Ignore our product-card add button here;
        it already has its own sync logic.
      */

      if (
        target.closest(
          SELECTORS.addButton
        )
      ) {
        return;
      }


      const button =
        target.closest(
          'button, a, [role="button"]'
        );


      if (!button) return;


      const text =
        [
          button.getAttribute(
            'aria-label'
          ) || '',

          button.getAttribute(
            'title'
          ) || '',

          button.textContent || ''
        ]
          .join(' ')
          .toLowerCase();


      const looksLikeCartAction =
        text.includes('remove') ||
        text.includes('delete') ||
        text.includes('increase') ||
        text.includes('decrease') ||
        text.includes('plus') ||
        text.includes('minus') ||
        text.trim() === '+' ||
        text.trim() === '−' ||
        text.trim() === '-';


      if (!looksLikeCartAction) {
        return;
      }


      /*
        Only verify after likely cart action.
      */

      window.setTimeout(
        syncCartCount,
        250
      );


      window.setTimeout(
        syncCartCount,
        650
      );


      window.setTimeout(
        syncCartCount,
        1200
      );

    }
  );


  /* ==========================================================================
     ESSENTIAL DOM MUTATION FALLBACK

     Watches only cart/drawer-like DOM mutations.
     This helps if Essential updates via an internal mechanism we don't see.
     ========================================================================== */

  const installDrawerObserver = () => {

    if (!document.body) return;


    const observer =
      new MutationObserver(
        (mutations) => {

          for (
            const mutation
            of mutations
          ) {

            const target =
              mutation.target;


            if (
              !(target instanceof Element)
            ) {
              continue;
            }


            const cartContext =
              target.closest(
                [
                  '[class*="essential"]',
                  '[id*="essential"]',
                  '[data-essential]',
                  '[class*="cart-drawer"]',
                  '[id*="cart-drawer"]',
                  '[class*="cartDrawer"]',
                  '[id*="cartDrawer"]'
                ].join(',')
              );


            if (!cartContext) {
              continue;
            }


            scheduleSync(
              180
            );


            break;

          }

        }
      );


    observer.observe(
      document.body,
      {
        subtree: true,
        childList: true,
        characterData: true
      }
    );

  };


  /* ==========================================================================
     PAGE RESTORE / TAB RESTORE
     ========================================================================== */

  window.addEventListener(
    'pageshow',
    () => {

      scheduleSync(
        40
      );

    }
  );


  document.addEventListener(
    'visibilitychange',
    () => {

      if (
        document.visibilityState ===
        'visible'
      ) {

        scheduleSync(
          50
        );

      }

    }
  );


  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  const initialize = () => {

    installFetchObserver();

    installXhrObserver();

    installDrawerObserver();

    syncCartCount();

  };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );

  } else {

    initialize();

  }


  /* ==========================================================================
     AFTER ALL APP SCRIPTS LOAD

     If Essential replaced window.fetch after our first initialization,
     wrap it again.
     ========================================================================== */

  window.addEventListener(
    'load',
    () => {

      installFetchObserver();

      installXhrObserver();

      scheduleSync(
        100
      );

    },
    {
      once: true
    }
  );

})();